import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Calendar, MapPin, Loader2, Users, Check } from "lucide-react";
import { api } from "../lib/api";
import EmptyState from "../components/EmptyState";
import { Skeleton } from "../components/Skeleton";
import { formatDateTime } from "../lib/date";

const EMPTY_ILLUSTRATION =
  "https://static.prod-images.emergentagent.com/jobs/1708729f-9648-4829-9f8b-880cdb94ab73/images/2b86f2d245fe343ea2b1e71873b523c99246b5949c103da0a1367b1a9a052049.png";

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/events")
      .then((r) => {
        setEvents(r.data.events || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const mine = events.filter((e) => e.my_status === "attending" || e.my_status === "maybe");
  const upcoming = events.filter((e) => !e.my_status || e.my_status === "declined");

  return (
    <div data-testid="events-page">
      <div className="mb-6">
        <h1
          className="text-[28px] sm:text-[32px] font-bold leading-tight"
          style={{ fontFamily: "'Sora', sans-serif" }}
        >
          Événements
        </h1>
        <p className="text-sm mt-1" style={{ color: "#666" }}>
          Les rendez-vous APOLLO à venir, à hauteur de votre périmètre.
        </p>
      </div>

      {loading && (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="bg-white rounded-[12px] p-5"
              style={{ border: "1px solid #E5E5E5" }}
            >
              <Skeleton className="w-20 h-5 mb-3" />
              <Skeleton className="w-3/4 h-5 mb-2" />
              <Skeleton className="w-1/2 h-4" />
            </div>
          ))}
        </div>
      )}

      {!loading && events.length === 0 && (
        <EmptyState
          illustration={EMPTY_ILLUSTRATION}
          title="Aucun événement à venir"
          description="Dès qu'un événement est publié sur votre périmètre, vous le retrouverez ici."
          testid="events-empty"
        />
      )}

      {!loading && mine.length > 0 && (
        <section>
          <h2
            className="text-lg font-bold mb-3 inline-flex items-center gap-2"
            style={{ fontFamily: "'Sora', sans-serif" }}
            data-testid="events-mine-header"
          >
            Mes inscriptions
            <span
              className="text-xs font-semibold px-2 py-0.5 rounded-full"
              style={{ background: "#FFF7ED", color: "#9A3412" }}
            >
              {mine.length}
            </span>
          </h2>
          <div className="space-y-3 mb-8">
            {mine.map((e) => (
              <EventCard key={e.post_id} e={e} />
            ))}
          </div>
        </section>
      )}

      {!loading && upcoming.length > 0 && (
        <section>
          <h2
            className="text-lg font-bold mb-3"
            style={{ fontFamily: "'Sora', sans-serif" }}
            data-testid="events-upcoming-header"
          >
            À venir
          </h2>
          <div className="space-y-3">
            {upcoming.map((e) => (
              <EventCard key={e.post_id} e={e} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function EventCard({ e }) {
  return (
    <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
      <Link
        to={`/posts/${e.post_id}`}
        className="block bg-white rounded-[12px] p-5 transition-shadow hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)]"
        style={{ border: "1px solid #E5E5E5" }}
        data-testid={`event-card-${e.post_id}`}
      >
        <div className="flex items-start gap-3">
          <div
            className="w-12 h-12 rounded-lg flex flex-col items-center justify-center flex-shrink-0"
            style={{ background: "#FFF7ED" }}
          >
            <Calendar color="#F77F00" size={20} />
          </div>
          <div className="flex-1 min-w-0">
            <h3
              className="font-bold mb-1 leading-snug"
              style={{ fontFamily: "'Sora', sans-serif" }}
            >
              {e.title}
            </h3>
            <div className="text-sm" style={{ color: "#666" }}>
              {formatDateTime(e.event_date)}
            </div>
            {e.event_location && (
              <div
                className="text-xs mt-1 flex items-center gap-1"
                style={{ color: "#666" }}
              >
                <MapPin size={11} />
                {e.event_location}
              </div>
            )}
            <div className="text-xs mt-2 inline-flex items-center gap-1" style={{ color: "#666" }}>
              <Users size={11} /> {e.attendees_count || 0} participant{(e.attendees_count || 0) > 1 ? "s" : ""}
            </div>
          </div>
          {e.my_status === "attending" && (
            <span
              className="text-xs font-semibold px-2 py-1 rounded-full inline-flex items-center gap-1"
              style={{ background: "#D1FAE5", color: "#065F46" }}
            >
              <Check size={11} /> Inscrit
            </span>
          )}
          {e.my_status === "maybe" && (
            <span
              className="text-xs font-semibold px-2 py-1 rounded-full"
              style={{ background: "#FEF3C7", color: "#92400E" }}
            >
              Peut-être
            </span>
          )}
        </div>
      </Link>
    </motion.div>
  );
}
